<template>
  <section class="atlas-page">
    <div class="atlas-shell">
      <div class="topbar">
        <div class="crumbs">
          <button type="button" class="nav-chip" @click="goPortal">{{ t("portal") }}</button>
          <button v-if="topic?.seriesSlug" type="button" class="nav-chip subtle" @click="goSeries(topic.seriesSlug)">
            {{ topic?.seriesTitle ? pickLangText(topic.seriesTitle, lang) : t("series") }}
          </button>
        </div>
        <div class="lang-pill">
          <button
            v-for="option in languages"
            :key="option"
            type="button"
            :class="['lang-chip', { active: option === lang }]"
            @click="setLang(option)"
          >
            {{ option.toUpperCase() }}
          </button>
        </div>
      </div>

      <p v-if="loading" class="state-card">{{ t("loading") }}</p>
      <p v-else-if="errorMessage" class="error-panel">{{ errorMessage }}</p>

      <template v-else-if="topic">
        <header class="hero-card panel-card">
          <div class="hero-copy">
            <p class="eyebrow">{{ t("eyebrow") }}</p>
            <div class="hero-topline">
              <span class="soft-badge">{{ topic.episodeNo ? `EP ${topic.episodeNo}` : getCategoryLabel(topic.topicType, lang) }}</span>
              <span class="status-chip">{{ getStatusLabel(topic.status, lang) }}</span>
            </div>
            <h1>{{ pickLangText(topic.title, lang) }}</h1>
            <p class="lead">{{ pickLangText(topic.summary, lang) }}</p>
            <div class="chip-row">
              <span v-for="tag in topic.tags || []" :key="tag" class="ink-chip">{{ tag }}</span>
            </div>
          </div>

          <aside class="hero-meta">
            <div class="meta-box">
              <span class="meta-label">{{ t("published") }}</span>
              <strong>{{ formatDate(topic.publishedAt) }}</strong>
            </div>
            <div class="meta-box">
              <span class="meta-label">{{ t("recordingStatus") }}</span>
              <strong>{{ topic.content?.recording_status || '-' }}</strong>
            </div>
            <div class="meta-box wide">
              <span class="meta-label">{{ t("source") }}</span>
              <strong>{{ topic.content?.source_file || '-' }}</strong>
            </div>
            <div v-if="nextTopicLabel" class="meta-box wide">
              <span class="meta-label">{{ t("nextTopic") }}</span>
              <strong>{{ nextTopicLabel }}</strong>
            </div>
          </aside>
        </header>

        <section v-if="youtubeIds.length || bilibiliIds.length" class="panel-card media-card">
          <div class="section-head">
            <div>
              <p class="section-tag">{{ t("mediaTag") }}</p>
              <h2>{{ t("videos") }}</h2>
            </div>
            <span>{{ t("videoHint") }}</span>
          </div>
          
          <div class="video-grid">
            <div v-for="videoId in youtubeIds" :key="`yt-${videoId}`" class="video-frame">
              <iframe :src="youtubeEmbedUrl(videoId)" allowfullscreen loading="lazy"></iframe>
            </div>
            <div v-for="videoId in bilibiliIds" :key="`bi-${videoId}`" class="video-frame">
              <iframe :src="bilibiliEmbedUrl(videoId)" allowfullscreen loading="lazy"></iframe>
            </div>
          </div>
        </section>

        <section class="panel-card media-card" v-if="topic.seriesSlug =='it-kenshi-kendo-japanese'">
          <div class="section-head">
            <div>
              <p class="section-tag">{{ t("questionTag") }}</p>
              <h2>{{ t("questionSet") }}</h2>
            </div>
            <div class="question-head-meta">
              <span>{{ questions.length }} {{ t("items") }}</span>
              <button v-if="questions.length" type="button" class="nav-chip subtle" @click="restartQuiz">
                {{ t("restartQuiz") }}
              </button>
            </div>
          </div>

          <p class="lead question-lead">{{ t("questionHint") }}</p>

          <p v-if="questionsLoading" class="state-card inline-state">{{ t("questionLoading") }}</p>
          <p v-else-if="questionsError" class="error-panel inline-state">{{ questionsError }}</p>
          <div v-else-if="questions.length" class="question-stage">
            <div v-if="!quizFinished" class="question-overview">
              <span class="soft-badge">{{ t("questionProgress", { current: currentQuestionIndex + 1, total: questions.length }) }}</span>
              <span class="ink-chip">{{ t("weightedScore") }} {{ earnedWeight }}/{{ totalWeight }}</span>
            </div>

            <article v-if="quizFinished" class="quiz-card quiz-result-card">
              <div class="hero-topline">
                <span class="soft-badge">{{ t("quizDoneBadge") }}</span>
                <span class="status-chip">{{ t("weightedScore") }} {{ weightedScorePercent }}%</span>
              </div>

              <h3>{{ t("quizDoneTitle") }}</h3>
              <p>{{ t("quizDoneText") }}</p>

              <div class="result-metrics">
                <div class="metric-box">
                  <span class="meta-label">{{ t("weightedScore") }}</span>
                  <strong>{{ weightedScorePercent }}%</strong>
                </div>
                <div class="metric-box">
                  <span class="meta-label">{{ t("scoreDetail") }}</span>
                  <strong>{{ earnedWeight }} / {{ totalWeight }}</strong>
                </div>
                <div class="metric-box">
                  <span class="meta-label">{{ t("firstTryCorrect") }}</span>
                  <strong>{{ firstTryCorrectCount }} / {{ questions.length }}</strong>
                </div>
              </div>

              <button type="button" class="nav-chip subtle result-ack-btn" @click="acknowledgeQuizCompletion">
                {{ t("quizDoneAck") }}
              </button>
            </article>

            <article v-else-if="currentQuestion" :key="currentQuestion.quesId" class="quiz-card current-quiz-card">
              <div class="hero-topline">
                <span class="soft-badge">{{ getQuestionTypeLabel(currentQuestion.questionType) }}</span>
                <span class="soft-badge">{{ t("questionWeight", { weight: getQuestionWeight(currentQuestion) }) }}</span>
                <span class="ink-chip">{{ getQuestionCategoryLabel(currentQuestion.questionCategory) }}</span>
              </div>

              <h3>{{ currentQuestion.displayOrder }}. {{ pickLangText(currentQuestion.questionText, lang) }}</h3>

              <div class="question-options">
                <button
                  v-for="option in currentQuestion.options || []"
                  :key="`${currentQuestion.quesId}-${option.key}`"
                  type="button"
                  :class="['question-option', getOptionState(currentQuestion, option.key)]"
                  :disabled="currentFeedbackState === 'correct'"
                  @click="answerQuestion(currentQuestion, option.key)"
                >
                  <span class="option-key">{{ option.key }}</span>
                  <span>{{ pickLangText(option.text, lang) }}</span>
                </button>
              </div>

              <div v-if="currentFeedbackState" class="question-feedback">
                <p :class="['feedback-state', { wrong: currentFeedbackState !== 'correct' }]">
                  {{ currentFeedbackState === 'correct' ? t("questionCorrect") : t("questionWrong") }}
                </p>

                <p v-if="currentFeedbackState !== 'correct'" class="feedback-answer">
                  {{ t("retryQuestionHint") }}
                </p>

                <template v-else>
                  <p class="feedback-answer">
                    {{ t("correctAnswer") }}: {{ findOptionLabel(currentQuestion, currentQuestion.correctOptionKey) }}
                  </p>
                  <p v-if="currentQuestion.answerExplanation">{{ pickLangText(currentQuestion.answerExplanation, lang) }}</p>
                  <button type="button" class="nav-chip subtle retry-chip" @click="goToNextQuestion">
                    {{ isLastQuestion ? t("finishQuiz") : t("nextQuestion") }}
                  </button>
                </template>
              </div>
            </article>
          </div>
          <div v-else-if="questionsLoaded" class="empty-state">{{ t("emptyQuestions") }}</div>
        </section>

        <section class="split-stage">
          <article class="panel-card section-card-wrap">
            <div class="section-head">
              <div>
                <p class="section-tag">{{ t("sectionsTag") }}</p>
                <h2>{{ t("sections") }}</h2>
              </div>
              <span>{{ sections.length }} {{ t("items") }}</span>
            </div>
            <div class="section-list">
              <article
                v-for="section in sections"
                :key="section.key || section.ja || section.zh || section.en"
                class="section-card"
              >
                <h3>{{ section.key || t("section") }}</h3>
                <p>{{ pickLangText(section, lang) }}</p>
              </article>
            </div>
          </article>

          <article class="panel-card term-card-wrap">
            <div class="section-head">
              <div>
                <p class="section-tag">{{ t("termsTag") }}</p>
                <h2>{{ t("relatedTerms") }}</h2>
              </div>
              <span>{{ topic.relatedTerms?.length || 0 }} {{ t("items") }}</span>
            </div>

            <div v-if="!topic.relatedTerms?.length" class="empty-state">
              {{ t("emptyTerms") }}
            </div>

            <div v-else class="term-list">
              <article v-for="item in topic.relatedTerms" :key="item.topicTermId" class="term-card">
                <div class="hero-topline">
                  <span class="soft-badge">{{ item.term.term }}</span>
                  <span class="status-chip">{{ item.relationType }}</span>
                </div>
                <h3>{{ pickLangText(item.term.label, lang) }}</h3>
                <p class="term-pron">{{ item.term.katakana || item.term.romaji || item.term.py || '-' }}</p>
                <p>{{ summarizeText(item.term.description, lang, 150) }}</p>
              </article>
            </div>
          </article>
        </section>
      </template>
    </div>
  </section>
</template>

<script setup>
import axios from "axios";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  API_BASE,
  ensureApiArray,
  ensureApiObject,
  bilibiliEmbedUrl,
  formatDate,
  getCategoryLabel,
  getStatusLabel,
  pickLangText,
  summarizeText,
  toArray,
  youtubeEmbedUrl,
} from "./portalShared";
import { useSiteLanguage } from "../../store/siteLanguage";

const route = useRoute();
const router = useRouter();
const { lang, languages, setLang } = useSiteLanguage();

const questions = ref([]);
const questionsLoading = ref(false);
const questionsError = ref("");
const questionsLoaded = ref(false);
const currentQuestionIndex = ref(0);
const currentSelectedOptionKey = ref("");
const currentFeedbackState = ref("");
const questionAttempts = ref({});
const awardedQuestionWeights = ref({});
const quizFinished = ref(false);

const copy = {
  ja: {
    portal: "ポータル",
    series: "シリーズ",
    loading: "トピックを読込中...",
    eyebrow: "TOPIC SHEET",
    published: "公開日",
    recordingStatus: "制作状況",
    source: "原稿ファイル",
    nextTopic: "次回予告",
    mediaTag: "動画レビュー",
    videos: "動画",
    videoHint: "公開後の埋め込み確認エリア。",
    sectionsTag: "SCRIPT SECTIONS",
    sections: "Sections",
    termsTag: "TERM CONNECTIONS",
    relatedTerms: "Related Terms",
    items: "件",
    section: "セクション",
    emptyTerms: "このトピックに関連術語はまだありません。",
    questionTag: "KANA PRACTICE",
    questionSet: "IT剣士・剣道用語問題集",
    questionHint: "1問ずつ順番に答えて、正解したら次へ進みます。得点は重み付きで集計します。",
    questionLoading: "問題を読込中...",
    emptyQuestions: "このトピックの問題はまだありません。",
    restartQuiz: "最初から",
    retryQuestion: "次へ進む",
    retryQuestionHint: "まだ次へ進めません。正解するまで選び直してください。",
    questionCorrect: "正解です。",
    questionWrong: "不正解です。",
    correctAnswer: "正しい答え",
    questionTypeSingleChoice: "四択",
    questionTypeTrueFalse: "正誤",
    questionCategory: "問題",
    questionCategoryLanguage: "言語",
    questionCategoryKendou: "剣道",
    questionProgress: "{current} / {total} 問",
    weightedScore: "加重点",
    questionWeight: "重み {weight}",
    nextQuestion: "次の問題へ",
    finishQuiz: "結果を見る",
    quizDoneBadge: "COMPLETE",
    quizDoneTitle: "全部終わりました。",
    quizDoneText: "今回の問題はこれで終了です。確認したら最初の問題に戻れます。",
    quizDoneAck: "わかりました",
    scoreDetail: "獲得重み",
    firstTryCorrect: "初回正解",
  },
  zh: {
    portal: "门户",
    series: "系列",
    loading: "正在读取主题...",
    eyebrow: "TOPIC SHEET",
    published: "发布日期",
    recordingStatus: "制作状态",
    source: "原稿文件",
    nextTopic: "下一集",
    mediaTag: "视频预览",
    videos: "视频",
    videoHint: "发布后可在这里检查嵌入效果。",
    sectionsTag: "SCRIPT SECTIONS",
    sections: "Sections",
    termsTag: "TERM CONNECTIONS",
    relatedTerms: "Related Terms",
    items: "条",
    section: "区段",
    emptyTerms: "这个主题还没有关联术语。",
    questionTag: "KANA PRACTICE",
    questionSet: "IT剑士・剑道用语题集",
    questionHint: "题目会按顺序一题一题出现，答对后才能进入下一题，最后按权重计算得分。",
    questionLoading: "正在读取题目...",
    emptyQuestions: "这个主题暂时还没有题目。",
    restartQuiz: "重新开始",
    retryQuestion: "下一题",
    retryQuestionHint: "这题还没答对，请继续重试。",
    questionCorrect: "回答正确。",
    questionWrong: "这题答错了。",
    correctAnswer: "正确答案",
    questionTypeSingleChoice: "单选",
    questionTypeTrueFalse: "判断",
    questionCategory: "题型",
    questionCategoryLanguage: "语言",
    questionCategoryKendou: "剑道",
    questionProgress: "第 {current} / {total} 题",
    weightedScore: "加权得分",
    questionWeight: "权重 {weight}",
    nextQuestion: "进入下一题",
    finishQuiz: "查看结果",
    quizDoneBadge: "COMPLETE",
    quizDoneTitle: "已经做完了。",
    quizDoneText: "这一轮练习已经结束，确认后会回到第一题重新开始。",
    quizDoneAck: "知道了",
    scoreDetail: "得分权重",
    firstTryCorrect: "首次答对",
  },
  en: {
    portal: "Portal",
    series: "Series",
    loading: "Loading topic...",
    eyebrow: "TOPIC SHEET",
    published: "Published",
    recordingStatus: "Production",
    source: "Source file",
    nextTopic: "Next topic",
    mediaTag: "VIDEO REVIEW",
    videos: "Videos",
    videoHint: "Embed review area after publishing.",
    sectionsTag: "SCRIPT SECTIONS",
    sections: "Sections",
    termsTag: "TERM CONNECTIONS",
    relatedTerms: "Related Terms",
    items: "items",
    section: "section",
    emptyTerms: "This topic does not have related terms yet.",
    questionTag: "KANA PRACTICE",
    questionSet: "IT Kenshi: Kendo Terminology Quiz Series",
    questionHint: "Questions appear one at a time. Answer correctly to unlock the next one, and the final score is weighted.",
    questionLoading: "Loading questions...",
    emptyQuestions: "No questions have been published for this topic yet.",
    restartQuiz: "Start over",
    retryQuestion: "Next question",
    retryQuestionHint: "This one is not correct yet. Keep trying until you get it right.",
    questionCorrect: "Correct.",
    questionWrong: "Not quite.",
    correctAnswer: "Correct answer",
    questionTypeSingleChoice: "Multiple choice",
    questionTypeTrueFalse: "True / False",
    questionCategory: "Category",
    questionCategoryLanguage: "Language",
    questionCategoryKendou: "Kendo",
    questionProgress: "Question {current} / {total}",
    weightedScore: "Weighted score",
    questionWeight: "Weight {weight}",
    nextQuestion: "Next question",
    finishQuiz: "View result",
    quizDoneBadge: "COMPLETE",
    quizDoneTitle: "You have finished all questions.",
    quizDoneText: "This round is complete. After confirming, you will return to the first question.",
    quizDoneAck: "Got it",
    scoreDetail: "Weight earned",
    firstTryCorrect: "First-try correct",
  },
  
};

function t(key, params) {
  const value = copy[lang.value]?.[key] || key;
  if (!params || typeof value !== "string") {
    return value;
  }

  return value.replace(/\{(\w+)\}/g, (_, token) => `${params[token] ?? ""}`);
}

const topic = ref(null);
const loading = ref(true);
const errorMessage = ref("");

const sections = computed(() => toArray(topic.value?.content?.sections));
const youtubeIds = computed(() => toArray(topic.value?.youtubeVideoId));
const bilibiliIds = computed(() => toArray(topic.value?.bilibiliVideoId));
const nextTopicLabel = computed(() => pickLangText(topic.value?.content?.next_topic, lang.value, ""));
const currentQuestion = computed(() => questions.value[currentQuestionIndex.value] || null);
const isLastQuestion = computed(() => currentQuestionIndex.value >= Math.max(questions.value.length - 1, 0));
const totalWeight = computed(() => questions.value.reduce((sum, question) => sum + getQuestionWeight(question), 0));
const earnedWeight = computed(() => questions.value.reduce((sum, question) => sum + Number(awardedQuestionWeights.value[question.quesId] || 0), 0));
const weightedScorePercent = computed(() => (totalWeight.value ? Math.round((earnedWeight.value / totalWeight.value) * 100) : 0));
const firstTryCorrectCount = computed(() => Object.values(awardedQuestionWeights.value).filter((value) => Number(value) > 0).length);

async function loadTopic() {
  loading.value = true;
  errorMessage.value = "";

  try {
    const response = await axios.get(`${API_BASE}/api/kendou/topics/${encodeURIComponent(route.params.slug)}`, {
      timeout: 10000,
    });
    topic.value = ensureApiObject(response, "Kendou topic API");
    restartQuiz();
    if (topic.value?.seriesSlug === "it-kenshi-kendo-japanese") {
      await loadQuestions();
    } else {
      questions.value = [];
      questionsLoaded.value = false;
      questionsError.value = "";
    }
  } catch (error) {
    console.error(error);
    topic.value = null;
    questions.value = [];
    questionsLoaded.value = false;
    questionsError.value = "";
    errorMessage.value = error?.response?.status === 404 ? "Topic not found." : error?.message || "Failed to load topic.";
  } finally {
    loading.value = false;
  }
}

async function loadQuestions() {
  restartQuiz();
  questions.value = [];
  questionsLoaded.value = false;
  questionsError.value = "";
  questionsLoading.value = false;
  if (!topic.value) return;
  questionsLoading.value = true;
  try {
    const response = await axios.get(`${API_BASE}/api/kendou/topics/${topic.value.topicId}/questions`, {
      timeout: 10000,
    });
    questions.value = ensureApiArray(response, "Kendou topic questions API");
    questionsLoaded.value = true;
  } catch (error) {
    console.error(error);
    questionsError.value = error?.message || "Failed to load questions.";
    questionsLoaded.value = true;
  } finally {
    questionsLoading.value = false;
  }
}

function restartQuiz() {
  currentQuestionIndex.value = 0;
  currentSelectedOptionKey.value = "";
  currentFeedbackState.value = "";
  questionAttempts.value = {};
  awardedQuestionWeights.value = {};
  quizFinished.value = false;
}

function acknowledgeQuizCompletion() {
  restartQuiz();
}

function answerQuestion(question, optionKey) {
  if (!currentQuestion.value || question.quesId !== currentQuestion.value.quesId || currentFeedbackState.value === "correct") {
    return;
  }

  currentSelectedOptionKey.value = optionKey;

  const attempts = (questionAttempts.value[question.quesId] || 0) + 1;
  questionAttempts.value = {
    ...questionAttempts.value,
    [question.quesId]: attempts,
  };

  if (optionKey === question.correctOptionKey) {
    currentFeedbackState.value = "correct";
    awardedQuestionWeights.value = {
      ...awardedQuestionWeights.value,
      [question.quesId]: attempts === 1 ? getQuestionWeight(question) : 0,
    };
    return;
  }

  currentFeedbackState.value = "wrong";
}

function goToNextQuestion() {
  if (currentFeedbackState.value !== "correct") {
    return;
  }

  if (isLastQuestion.value) {
    quizFinished.value = true;
    return;
  }

  currentQuestionIndex.value += 1;
  currentSelectedOptionKey.value = "";
  currentFeedbackState.value = "";
}

function getQuestionTypeLabel(questionType) {
  return questionType === "true_false" ? t("questionTypeTrueFalse") : t("questionTypeSingleChoice");
}

function getQuestionCategoryLabel(questionCategory) {
  if (questionCategory === "language") {
    return t("questionCategoryLanguage");
  }

  if (questionCategory === "kendou") {
    return t("questionCategoryKendou");
  }

  return t("questionCategory");
}

function findOptionLabel(question, optionKey) {
  const option = toArray(question?.options).find((item) => item?.key === optionKey);
  if (!option) {
    return optionKey || "-";
  }

  return `${option.key}. ${pickLangText(option.text, lang.value)}`;
}

function getQuestionWeight(question) {
  const rawWeight = Number(question?.weight ?? question?.scoreWeight);
  if (Number.isFinite(rawWeight) && rawWeight > 0) {
    return rawWeight;
  }

  return 1;
}

function getOptionState(question, optionKey) {
  if (!currentQuestion.value || question.quesId !== currentQuestion.value.quesId || !currentSelectedOptionKey.value) {
    return "";
  }

  if (currentFeedbackState.value === "correct" && optionKey === currentSelectedOptionKey.value) {
    return "correct";
  }

  if (currentFeedbackState.value === "wrong" && optionKey === currentSelectedOptionKey.value) {
    return "wrong";
  }

  return "";
}

function goPortal() {
  router.push("/KendouPage/terminology");
}

function goSeries(slug) {
  router.push(`/KendouPage/series/${encodeURIComponent(slug)}`);
}

watch(() => route.params.slug, loadTopic);
onMounted(loadTopic);
</script>

<style scoped>
.atlas-page {
  --paper: rgba(251, 245, 235, 0.92);
  --paper-strong: rgba(255, 250, 242, 0.98);
  --ink: #2f2116;
  --ink-soft: #5d4b3d;
  --line: rgba(182, 148, 110, 0.34);
  min-height: 100vh;
  padding: 20px;
  background:
    radial-gradient(circle at 16% 12%, rgba(255, 255, 255, 0.7), transparent 28%),
    radial-gradient(circle at 86% 82%, rgba(235, 190, 116, 0.28), transparent 26%),
    linear-gradient(135deg, #e7d8bf 0%, #efe6d3 24%, #f5eedf 48%, #eadab8 100%);
}

.atlas-shell {
  max-width: 1380px;
  margin: 0 auto;
  display: grid;
  gap: 18px;
}

.topbar,
.crumbs,
.hero-topline,
.chip-row,
.section-head {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: space-between;
}

.lang-pill {
  display: inline-flex;
  gap: 8px;
  padding: 8px;
  border: 1px solid rgba(191, 169, 139, 0.72);
  border-radius: 999px;
  background: rgba(255, 250, 245, 0.8);
  box-shadow: 0 10px 24px rgba(106, 75, 41, 0.08);
}

.lang-chip,
.nav-chip {
  border-radius: 999px;
  cursor: pointer;
}

.lang-chip {
  border: 1px solid transparent;
  background: transparent;
  color: #604d3d;
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 1rem;
  padding: 10px 16px;
  transition: transform 0.22s ease, box-shadow 0.22s ease, background-color 0.22s ease, color 0.22s ease, border-color 0.22s ease;
}

.lang-chip.active {
  background: linear-gradient(135deg, #b64926 0%, #8a2613 100%);
  color: #fff8f3;
}

.lang-chip:hover {
  transform: translateY(-2px) scale(1.02);
  box-shadow: 0 10px 20px rgba(115, 80, 43, 0.14);
}

.lang-chip:active {
  transform: translateY(0) scale(0.98);
}

.nav-chip {
  border: 1px solid rgba(177, 144, 108, 0.7);
  background: rgba(255, 249, 240, 0.88);
  color: #5b4636;
  padding: 10px 15px;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background-color 0.22s ease, opacity 0.22s ease;
}

.nav-chip.subtle {
  background: rgba(245, 236, 223, 0.95);
}

.crumbs:hover .nav-chip {
  opacity: 0.84;
  transform: translateX(3px);
}

.crumbs:hover .nav-chip:hover {
  opacity: 1;
  transform: translateX(-3px) translateY(-2px);
}

.nav-chip:hover {
  border-color: rgba(184, 94, 55, 0.62);
  background: rgba(255, 252, 246, 0.98);
  box-shadow: 0 12px 24px rgba(105, 74, 40, 0.12);
}

.nav-chip:active {
  transform: translateX(0) translateY(0) scale(0.985);
}

.panel-card,
.state-card,
.error-panel,
.section-card,
.term-card,
.media-card {
  border: 1px solid var(--line);
  border-radius: 28px;
  background: linear-gradient(180deg, var(--paper-strong) 0%, var(--paper) 100%);
  box-shadow:
    0 22px 44px rgba(118, 87, 49, 0.12),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

.hero-card,
.media-card,
.section-card-wrap,
.term-card-wrap,
.state-card,
.error-panel {
  padding: 20px 22px 22px;
}

.hero-card {
  display: grid;
  grid-template-columns: 1.35fr 0.85fr;
  gap: 18px;
}

.eyebrow,
.section-tag {
  margin: 0 0 10px;
  font-family: "Cormorant Garamond", "Times New Roman", serif;
  font-size: 14px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #9c341d;
}

.hero-card h1,
.section-head h2,
.section-card h3,
.term-card h3 {
  margin: 0;
  color: var(--ink);
}

.hero-card h1 {
  font-family: "Noto Serif JP", "Yu Mincho", Georgia, serif;
  font-size: clamp(2.4rem, 4.2vw, 4rem);
  line-height: 1.1;
}

.lead,
.hero-meta,
.section-card p,
.term-card p,
.section-head span,
.state-card,
.error-panel {
  color: var(--ink-soft);
  line-height: 1.8;
}

.hero-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  align-content: start;
}

.meta-box {
  border: 1px solid rgba(216, 180, 139, 0.58);
  border-radius: 20px;
  background: rgba(255, 248, 239, 0.82);
  padding: 16px;
  display: grid;
  gap: 6px;
  transition: transform 0.26s ease, opacity 0.26s ease, filter 0.26s ease, box-shadow 0.26s ease, border-color 0.26s ease;
}

.meta-box.wide {
  grid-column: 1 / -1;
}

.meta-label {
  font-size: 0.8rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7a5d44;
}

.meta-box strong {
  color: var(--ink);
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 1.24rem;
  word-break: break-word;
}

.soft-badge,
.status-chip,
.ink-chip {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 6px 11px;
  font-size: 0.78rem;
  letter-spacing: 0.06em;
}

.soft-badge {
  background: rgba(235, 220, 201, 0.9);
  color: #6d5138;
}

.status-chip {
  background: linear-gradient(135deg, #442616 0%, #24130b 100%);
  color: #fff7f0;
}

.ink-chip {
  background: rgba(245, 233, 216, 0.95);
  color: #705940;
}

.video-grid,
.section-list,
.term-list,
.question-stage,
.split-stage {
  display: grid;
  gap: 14px;
}

.video-grid {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.split-stage {
  grid-template-columns: 1.15fr 0.85fr;
}

.video-frame {
  position: relative;
  padding-bottom: 56.25%;
  overflow: hidden;
  border-radius: 20px;
  border: 1px solid rgba(205, 175, 140, 0.75);
  background: #1a120a;
  transition: transform 0.26s ease, opacity 0.26s ease, filter 0.26s ease, box-shadow 0.26s ease, border-color 0.26s ease;
}

.video-frame iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.section-card,
.term-card,
.quiz-card {
  padding: 16px 18px 18px;
  transition: transform 0.26s ease, opacity 0.26s ease, filter 0.26s ease, box-shadow 0.26s ease, border-color 0.26s ease;
}

.quiz-card {
  border: 1px solid rgba(205, 175, 140, 0.62);
  border-radius: 22px;
  background: rgba(255, 249, 242, 0.92);
  display: grid;
  gap: 14px;
}

.current-quiz-card,
.quiz-result-card {
  max-width: 860px;
}

.quiz-card h3,
.question-feedback p {
  margin: 0;
}

.question-overview,
.result-metrics {
  display: grid;
  gap: 12px;
}

.question-overview {
  grid-template-columns: repeat(2, max-content);
  align-items: center;
}

.result-metrics {
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.metric-box {
  border: 1px solid rgba(216, 180, 139, 0.58);
  border-radius: 20px;
  background: rgba(255, 248, 239, 0.82);
  padding: 16px;
  display: grid;
  gap: 6px;
}

.metric-box strong {
  color: var(--ink);
  font-family: "Cormorant Garamond", Georgia, serif;
  font-size: 1.4rem;
}

.result-ack-btn {
  justify-self: flex-start;
}

.question-options,
.question-feedback {
  display: grid;
  gap: 10px;
}

.question-option {
  width: 100%;
  border: 1px solid rgba(194, 160, 121, 0.58);
  border-radius: 18px;
  background: rgba(255, 252, 247, 0.94);
  color: var(--ink);
  cursor: pointer;
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 14px 16px;
  font: inherit;
  text-align: left;
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease, background-color 0.22s ease, opacity 0.22s ease;
}

.question-option:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(117, 78, 40, 0.14);
}

.question-option:disabled {
  cursor: default;
}

.question-option.correct {
  border-color: rgba(42, 109, 83, 0.48);
  background: rgba(231, 246, 237, 0.98);
}

.question-option.wrong {
  border-color: rgba(166, 51, 30, 0.42);
  background: rgba(253, 237, 231, 0.98);
}

.question-option.muted {
  opacity: 0.72;
}

.option-key {
  min-width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(232, 216, 196, 0.92);
  color: #6b4f35;
  font-size: 0.84rem;
  font-weight: 700;
}

.question-feedback {
  border-top: 1px solid rgba(194, 160, 121, 0.35);
  padding-top: 12px;
}

.feedback-state {
  color: #2a6d53;
  font-weight: 700;
}

.feedback-state.wrong {
  color: #a7331e;
}

.feedback-answer,
.question-lead {
  margin: 0;
}

.question-head-meta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.retry-chip {
  justify-self: flex-start;
}

.inline-state {
  padding: 16px 18px;
}

.term-pron {
  margin: 6px 0 0;
  color: #7c654c;
}

.empty-state {
  color: var(--ink-soft);
  line-height: 1.8;
}

.hero-meta:hover .meta-box,
.video-grid:hover .video-frame,
.section-list:hover .section-card,
.term-list:hover .term-card {
  opacity: 0.74;
  transform: translateY(5px) scale(0.992);
  filter: saturate(0.86);
}

.hero-meta:hover .meta-box:hover,
.video-grid:hover .video-frame:hover,
.section-list:hover .section-card:hover,
.term-list:hover .term-card:hover {
  opacity: 1;
  transform: translateY(-6px) scale(1.012);
  filter: saturate(1.04);
  border-color: rgba(184, 94, 55, 0.65);
  box-shadow:
    0 18px 30px rgba(117, 78, 40, 0.16),
    inset 0 1px 0 rgba(255, 255, 255, 0.75);
}

@media (max-width: 1080px) {
  .hero-card,
  .split-stage,
  .video-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .atlas-page {
    padding: 14px;
  }

  .topbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .lang-pill {
    align-self: flex-start;
  }

  .hero-meta {
    grid-template-columns: 1fr;
  }

  .question-overview {
    grid-template-columns: 1fr;
  }
}

@keyframes atlasFadeLift {
  from {
    opacity: 0;
    transform: translateY(28px) scale(0.985);
    filter: blur(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
    filter: blur(0);
  }
}

.hero-card,
.media-card,
.split-stage,
.state-card,
.error-panel,
.meta-box,
.video-frame,
.section-card,
.term-card,
.quiz-card {
  animation: atlasFadeLift 0.72s cubic-bezier(0.2, 0.8, 0.2, 1) both;
  will-change: transform, opacity, filter;
}

.hero-card {
  animation-delay: 0.06s;
}

.media-card {
  animation-delay: 0.16s;
}

.split-stage {
  animation-delay: 0.26s;
}

.hero-meta .meta-box:nth-child(1) {
  animation-delay: 0.16s;
}

.hero-meta .meta-box:nth-child(2) {
  animation-delay: 0.22s;
}

.hero-meta .meta-box:nth-child(3) {
  animation-delay: 0.28s;
}

.hero-meta .meta-box:nth-child(4) {
  animation-delay: 0.34s;
}

.video-grid .video-frame:nth-child(1) {
  animation-delay: 0.22s;
}

.video-grid .video-frame:nth-child(2) {
  animation-delay: 0.3s;
}

.video-grid .video-frame:nth-child(3) {
  animation-delay: 0.38s;
}

.section-list .section-card:nth-child(1) {
  animation-delay: 0.32s;
}

.section-list .section-card:nth-child(2) {
  animation-delay: 0.4s;
}

.section-list .section-card:nth-child(3) {
  animation-delay: 0.48s;
}

.section-list .section-card:nth-child(4) {
  animation-delay: 0.56s;
}

.section-list .section-card:nth-child(5) {
  animation-delay: 0.64s;
}

.term-list .term-card:nth-child(1) {
  animation-delay: 0.38s;
}

.term-list .term-card:nth-child(2) {
  animation-delay: 0.46s;
}

.term-list .term-card:nth-child(3) {
  animation-delay: 0.54s;
}

.term-list .term-card:nth-child(4) {
  animation-delay: 0.62s;
}

@media (prefers-reduced-motion: reduce) {
  .hero-card,
  .media-card,
  .split-stage,
  .state-card,
  .error-panel,
  .meta-box,
  .video-frame,
  .section-card,
  .term-card,
  .quiz-card,
  .lang-chip,
  .nav-chip,
  .question-option {
    animation: none;
    transition: none;
  }
}
</style>
